import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { bizuApi } from '../../api/bizu.api';
import { recipesApi } from '../../api/recipes.api';
import { useAuth } from '../../context/AuthContext';
import { ArrowLeftIcon, XMarkIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function KienUserRecipesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchRecipes = async () => {
    setLoading(true);
    try {
      const response = await bizuApi.getKienUserRecipes(userId);
      setData(response.data);
    } catch (err) {
      console.error('Error fetching user recipes:', err);
      setError('Failed to load user recipes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecipes();
  }, [userId]);

  const handleDelete = async () => {
    if (!selectedRecipe) return;

    setDeleting(true);
    try {
      await recipesApi.delete(selectedRecipe._id);
      setShowDeleteConfirm(false);
      setSelectedRecipe(null);
      // Refresh the recipes list
      fetchRecipes();
    } catch (err) {
      console.error('Error deleting recipe:', err);
      alert('Failed to delete recipe');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="mb-6">
          <Button
            variant="secondary"
            onClick={() => navigate('/bizu/kien')}
            className="mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Kien
          </Button>
        </div>
        <div className="text-center py-8 text-gray-500">
          {error || 'No recipes found'}
        </div>
      </DashboardLayout>
    );
  }

  const { user, recipes, totalRecipes } = data;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <Button
          variant="secondary"
          onClick={() => navigate('/bizu/kien')}
          className="mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Kien
        </Button>
      </div>

      {/* User Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.fullName}'s Created Recipes</h1>
            <p className="text-gray-500">@{user.username}</p>
            {user.role && (
              <Badge variant={user.role === 'adult' ? 'info' : 'secondary'} className="mt-2">
                {user.role}
              </Badge>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Total Recipes Created</p>
            <p className="text-3xl font-bold text-gray-900">{totalRecipes}</p>
          </div>
        </div>
      </div>

      {/* Recipes List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            User Recipes ({recipes.length})
          </h2>
        </div>

        {recipes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No recipes created by this user.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Recipe Name (EN)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Recipe Name (ES)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Ingredients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recipes.map((recipe) => (
                  <tr
                    key={recipe._id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedRecipe(recipe)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {recipe.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recipe.name_es}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {recipe.isAI ? (
                        <Badge variant="info">AI Generated</Badge>
                      ) : recipe.is_main ? (
                        <Badge variant="success">Main Recipe</Badge>
                      ) : (
                        <Badge variant="secondary">Custom</Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {recipe.ingredients?.length || 0} ingredients
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {recipe.created_at
                        ? new Date(recipe.created_at).toLocaleDateString()
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setSelectedRecipe(null)}
            />

            {/* Modal Card */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-auto transform transition-all">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-t-2xl px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-white">{selectedRecipe.name}</h3>
                    <p className="text-primary-100 text-sm">{selectedRecipe.name_es}</p>
                  </div>
                  <button
                    onClick={() => setSelectedRecipe(null)}
                    className="text-white hover:text-primary-200 transition-colors"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Card Body */}
              <div className="px-6 py-5">
                {/* Type Badge */}
                <div className="mb-4">
                  {selectedRecipe.isAI ? (
                    <Badge variant="info">AI Generated</Badge>
                  ) : selectedRecipe.is_main ? (
                    <Badge variant="success">Main Recipe</Badge>
                  ) : (
                    <Badge variant="secondary">Custom Recipe</Badge>
                  )}
                </div>

                {/* Ingredients Section */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
                    Ingredients
                  </h4>
                  {selectedRecipe.ingredients?.length > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <ul className="space-y-2">
                        {selectedRecipe.ingredients.map((ing, index) => (
                          <li key={index} className="flex items-center justify-between text-sm">
                            <span className="text-gray-900 font-medium">
                              {ing.ingredient_one || ing.ingredient_many || '-'}
                            </span>
                            <span className="text-gray-500">
                              {ing.quantity ? `${ing.quantity} ${ing.unit || ''}`.trim() : '-'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm italic">No ingredients listed</p>
                  )}
                </div>

                {/* Creation Date */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Created</span>
                    <span className="text-gray-900 font-medium">
                      {selectedRecipe.created_at
                        ? new Date(selectedRecipe.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })
                        : 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Footer */}
              <div className="bg-gray-50 rounded-b-2xl px-6 py-4">
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setSelectedRecipe(null)}
                    className="flex-1"
                  >
                    Close
                  </Button>
                  {isAdmin && (
                    <Button
                      variant="danger"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="flex-1"
                    >
                      <TrashIcon className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowDeleteConfirm(false)}
            />

            {/* Confirmation Card */}
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-auto p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <TrashIcon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Delete Recipe
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Are you sure you want to delete "<strong>{selectedRecipe?.name}</strong>"?
                  This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeleteConfirm(false)}
                    className="flex-1"
                    disabled={deleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDelete}
                    className="flex-1"
                    disabled={deleting}
                  >
                    {deleting ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
